import { supabase } from '../supabaseClient';
import { resolveStorageUrl } from '../utils/storageUrl';

// helper: ensure persons is always an array of trimmed strings
const normalizePersons = (persons) => {
  if (!persons) return [];
  if (Array.isArray(persons)) return persons.map(p => String(p).trim()).filter(Boolean);
  if (typeof persons === 'string') {
    return persons.split(',').map(s => s.trim()).filter(Boolean);
  }
  return [];
};

export const mediaService = {
  async getMediaData(editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      // Get media videos
      const { data: videos, error: videosError } = await supabase
        .from('media_videos')
        .select('*')
        .eq('edition_id', edition.id)
        .order('display_order', { ascending: true });

      if (videosError) {
        throw videosError;
      }

      return (videos || []).map(video => ({
        id: video.id,
        title: video.title,
        description: video.description,
        thumbnail: resolveStorageUrl(video.thumbnail_url, 'media-thumbnails', video.thumbnail_path),
        thumbnailPath: video.thumbnail_path,
        videoUrl: video.video_url,
        duration: video.duration,
        displayOrder: video.display_order,
        persons: video.persons || [] // NEW: return persons
      }));
    } catch (error) {
      throw error;
    }
  },

  async addVideo(videoData, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      // Get next display order
      const { data: maxOrder } = await supabase
        .from('media_videos')
        .select('display_order')
        .eq('edition_id', edition.id)
        .order('display_order', { ascending: false })
        .limit(1);

      const nextOrder = maxOrder && maxOrder.length > 0 ? maxOrder[0].display_order + 1 : 0;

      // Ensure video URL is in embed format
      const embedUrl = this.convertToEmbedUrl(videoData.videoUrl);

      const insertData = {
        edition_id: edition.id,
        title: videoData.title,
        description: videoData.description,
        thumbnail_path: videoData.thumbnailPath || '',
        thumbnail_url: videoData.thumbnail || '',
        video_url: embedUrl,
        duration: videoData.duration,
        display_order: nextOrder,
        persons: normalizePersons(videoData.persons) // normalized
      };

      const { data, error } = await supabase
        .from('media_videos')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async updateVideo(videoId, videoData) {
    try {
      // Ensure video URL is in embed format
      const embedUrl = this.convertToEmbedUrl(videoData.videoUrl);

      const updateData = {
        title: videoData.title,
        description: videoData.description,
        thumbnail_path: videoData.thumbnailPath || '',
        thumbnail_url: videoData.thumbnail || '',
        video_url: embedUrl,
        duration: videoData.duration,
        persons: normalizePersons(videoData.persons), // normalized
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('media_videos')
        .update(updateData)
        .eq('id', videoId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  convertToEmbedUrl(url) {
    // Convert various YouTube URL formats to embed format
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1].split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1].split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('youtube.com/embed/')) {
      return url; // Already in embed format
    }
    return url; // Return original URL for other platforms
  },

  async deleteVideo(videoId) {
    try {
      // Get video data to delete associated files
      const { data: video } = await supabase
        .from('media_videos')
        .select('thumbnail_path')
        .eq('id', videoId)
        .single();

      // Delete thumbnail file if it exists
      if (video?.thumbnail_path) {
        await this.deleteMediaFile(video.thumbnail_path);
      }

      const { error } = await supabase
        .from('media_videos')
        .delete()
        .eq('id', videoId);

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  },

  async uploadVideoThumbnail(file, editionYear) {
    try {
      // Generate unique filename
      const timestamp = Date.now();
      const fileName = `thumbnail-${timestamp}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const filePath = `${editionYear}/${fileName}`;

      // Upload to Supabase storage - using 'media-thumbnails' bucket
      const { data, error } = await supabase.storage
        .from('media-thumbnails')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('media-thumbnails')
        .getPublicUrl(filePath);

      return {
        path: filePath,
        url: urlData.publicUrl
      };
    } catch (error) {
      throw error;
    }
  },

  async uploadMediaImage(file, editionYear) {
    try {
      const timestamp = Date.now();
      const fileName = `image-${timestamp}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const filePath = `${editionYear}/${fileName}`;

      const { data, error } = await supabase.storage
        .from('media-thumbnails')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('media-thumbnails')
        .getPublicUrl(filePath);

      return {
        path: filePath,
        url: urlData.publicUrl
      };
    } catch (error) {
      throw error;
    }
  },

  async deleteMediaFile(filePath) {
    try {
      const { error } = await supabase.storage
        .from('media-thumbnails')
        .remove([filePath]);

      if (error) {
      }
    } catch (error) {
    }
  },

  async deleteImageFile(filePath) {
    try {
      const { error } = await supabase.storage
        .from('media-thumbnails')
        .remove([filePath]);

      if (error) {
      }
    } catch (error) {
    }
  },

  async getMediaImages(editionYear) {
    try {
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const { data: images, error: imagesError } = await supabase
        .from('media_images')
        .select('*')
        .eq('edition_id', edition.id)
        .order('display_order', { ascending: true });

      if (imagesError) {
        throw imagesError;
      }

      return (images || []).map(image => ({
        id: image.id,
        title: image.title,
        description: image.description,
        imageUrl: resolveStorageUrl(image.image_url, 'media-thumbnails', image.image_path),
        imagePath: image.image_path,
        displayOrder: image.display_order,
        persons: image.persons || [] // NEW: return persons
      }));
    } catch (error) {
      throw error;
    }
  },

  async getSocialMediaImages(editionYear) {
    try {
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const { data: images, error: imagesError } = await supabase
        .from('social_media_images')
        .select('*')
        .eq('edition_id', edition.id)
        .order('display_order', { ascending: true });

      if (imagesError) {
        throw imagesError;
      }

      return (images || []).map(image => ({
        id: image.id,
        title: image.title,
        description: image.description,
        imageUrl: resolveStorageUrl(image.image_url, 'media-thumbnails', image.image_path),
        imagePath: image.image_path,
        socialPlatform: image.social_platform,
        displayOrder: image.display_order,
        persons: image.persons || [] // NEW: return persons
      }));
    } catch (error) {
      throw error;
    }
  },

  async addMediaImage(imageData, editionYear) {
    try {
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const { data: maxOrder } = await supabase
        .from('media_images')
        .select('display_order')
        .eq('edition_id', edition.id)
        .order('display_order', { ascending: false })
        .limit(1);

      const nextOrder = maxOrder && maxOrder.length > 0 ? maxOrder[0].display_order + 1 : 0;

      const insertData = {
        edition_id: edition.id,
        title: imageData.title,
        description: imageData.description,
        image_path: imageData.imagePath || '',
        image_url: imageData.imageUrl || '',
        display_order: nextOrder,
        persons: normalizePersons(imageData.persons) // normalized
      };

      const { data, error } = await supabase
        .from('media_images')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async addSocialMediaImage(imageData, editionYear) {
    try {
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const { data: maxOrder } = await supabase
        .from('social_media_images')
        .select('display_order')
        .eq('edition_id', edition.id)
        .order('display_order', { ascending: false })
        .limit(1);

      const nextOrder = maxOrder && maxOrder.length > 0 ? maxOrder[0].display_order + 1 : 0;

      const insertData = {
        edition_id: edition.id,
        title: imageData.title,
        description: imageData.description,
        image_path: imageData.imagePath || '', // Will be empty for URL-based images
        image_url: imageData.imageUrl || '',
        social_platform: imageData.socialPlatform || '',
        display_order: nextOrder,
        persons: normalizePersons(imageData.persons) // normalized
      };

      const { data, error } = await supabase
        .from('social_media_images')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async deleteMediaImage(imageId) {
    try {
      const { data: image } = await supabase
        .from('media_images')
        .select('image_path')
        .eq('id', imageId)
        .single();

      if (image?.image_path) {
        await this.deleteImageFile(image.image_path);
      }

      const { error } = await supabase
        .from('media_images')
        .delete()
        .eq('id', imageId);

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  },

  async deleteSocialMediaImage(imageId) {
    try {
      const { data: image } = await supabase
        .from('social_media_images')
        .select('image_path')
        .eq('id', imageId)
        .single();

      // Only delete file if it was uploaded (has a path)
      if (image?.image_path) {
        await this.deleteImageFile(image.image_path);
      }

      const { error } = await supabase
        .from('social_media_images')
        .delete()
        .eq('id', imageId);

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  },

  // NEW: update existing gallery image (persist persons too)
  async updateMediaImage(imageId, imageData) {
    try {
      const updateData = {
        title: imageData.title,
        description: imageData.description,
        image_path: imageData.imagePath || '',
        image_url: imageData.imageUrl || '',
        persons: normalizePersons(imageData.persons),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('media_images')
        .update(updateData)
        .eq('id', imageId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  // NEW: update existing social image (persist persons too)
  async updateSocialMediaImage(imageId, imageData) {
    try {
      const updateData = {
        title: imageData.title,
        description: imageData.description,
        image_path: imageData.imagePath || '',
        image_url: imageData.imageUrl || '',
        social_platform: imageData.socialPlatform || '',
        persons: normalizePersons(imageData.persons),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('social_media_images')
        .update(updateData)
        .eq('id', imageId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },
};
